// ============================================================
// useIngredientDB — manages the editable ingredient database
// Persists to localStorage under 'sc_ingredient_db'
// Falls back to buildDefaultIngredientDB() on first load
// ============================================================
import { useState, useCallback, useMemo } from 'react';
import { buildDefaultIngredientDB, SECTIONS, DEFAULT_COST_UNIT, UNIT_TO_G, MACRO_DB } from '../data/ingredients';

const STORAGE_KEY   = 'sc_ingredient_db';
const DB_VERSION    = '4';  // bump when default DB structure changes to force refresh

// Fill in macros that MACRO_DB has gained since this database was written.
// Bumping DB_VERSION would rebuild from defaults and throw away every price the
// user has entered, so patch in place instead: only ever fills a blank, never
// overwrites a value someone set by hand.
function backfillMacros(db) {
  let filled = 0;
  Object.values(db).forEach(list => {
    if (!Array.isArray(list)) return;
    list.forEach(ing => {
      if (!ing || ing.macro || ing.isCustom) return;
      const macro = MACRO_DB[ing.name];
      if (macro) { ing.macro = macro; filled++; }
    });
  });
  return filled;
}

function loadDB() {
  try {
    const storedVersion = localStorage.getItem(STORAGE_KEY + '_version');
    const stored        = localStorage.getItem(STORAGE_KEY);
    // If version matches and data exists, use it
    if (stored && storedVersion === DB_VERSION) {
      const parsed = JSON.parse(stored);
      // Also check casing section exists and has entries
      if (parsed.casing && parsed.casing.length > 0) {
        if (backfillMacros(parsed)) {
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed)); } catch(e) {}
        }
        return parsed;
      }
    }
  } catch(e) {}
  // Fresh build — save version stamp
  const fresh = buildDefaultIngredientDB();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    localStorage.setItem(STORAGE_KEY + '_version', DB_VERSION);
  } catch(e) {}
  return fresh;
}

function saveDB(db) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
    localStorage.setItem(STORAGE_KEY + '_version', DB_VERSION);
  } catch(e) { console.warn('Could not save ingredient DB:', e); }
}

function genId() {
  return '_' + Math.random().toString(36).slice(2, 9);
}

export function useIngredientDB() {
  const [db, setDb] = useState(loadDB);

  // Persist whenever db changes
  const updateDb = useCallback((newDb) => {
    setDb(newDb);
    saveDB(newDb);
  }, []);

  // ── Get all names for a section (for dropdowns) ────────────
  const getNamesForSection = useCallback((sectionKey) => {
    return (db[sectionKey] || []).map(ing => ing.name);
  }, [db]);

  // ── Get a single ingredient by section + name ──────────────
  const getIngredient = useCallback((sectionKey, name) => {
    return (db[sectionKey] || []).find(ing => ing.name === name) || null;
  }, [db]);

  // ── Update a single ingredient field ──────────────────────
  const updateIngredient = useCallback((sectionKey, id, field, value) => {
    const newDb = { ...db };
    newDb[sectionKey] = (newDb[sectionKey] || []).map(ing =>
      ing.id === id ? { ...ing, [field]: value } : ing
    );
    updateDb(newDb);
  }, [db, updateDb]);

  // ── Update entire ingredient row ───────────────────────────
  const updateIngredientFull = useCallback((sectionKey, id, updatedIng) => {
    const newDb = { ...db };
    newDb[sectionKey] = (newDb[sectionKey] || []).map(ing =>
      ing.id === id ? { ...updatedIng, id } : ing
    );
    updateDb(newDb);
  }, [db, updateDb]);

  // ── Add new ingredient ─────────────────────────────────────
  const addIngredient = useCallback((sectionKey, ingData) => {
    const newDb = { ...db };
    const newIng = {
      id:          genId(),
      name:        ingData.name || 'New Ingredient',
      typMin:      parseFloat(ingData.typMin) || 0,
      typMax:      parseFloat(ingData.typMax) || 0,
      hardMax:     parseFloat(ingData.hardMax) || 0,
      note:        ingData.note || '',
      macro:       ingData.macro || null,
      costPerUnit: parseFloat(ingData.costPerUnit) || 0,
      costUnit:    ingData.costUnit || DEFAULT_COST_UNIT[sectionKey] || 'oz',
      isCustom:    true,
    };
    newDb[sectionKey] = [...(newDb[sectionKey] || []), newIng];
    updateDb(newDb);
    return newIng;
  }, [db, updateDb]);

  // ── Remove ingredient ──────────────────────────────────────
  const removeIngredient = useCallback((sectionKey, id) => {
    const newDb = { ...db };
    newDb[sectionKey] = (newDb[sectionKey] || []).filter(ing => ing.id !== id);
    updateDb(newDb);
  }, [db, updateDb]);

  // ── Search across all sections ─────────────────────────────
  const searchIngredients = useCallback((query) => {
    if (!query || query.trim() === '') return [];
    const q = query.toLowerCase();
    const results = [];
    SECTIONS.forEach(sect => {
      (db[sect.key] || []).forEach(ing => {
        if (ing.name.toLowerCase().includes(q) || (ing.note && ing.note.toLowerCase().includes(q))) {
          results.push({ ...ing, sectionKey: sect.key, sectionLabel: sect.label });
        }
      });
    });
    return results;
  }, [db]);

  // ── Cost per gram for a given ingredient ───────────────────
  const costPerGram = useCallback((sectionKey, name) => {
    const ing = getIngredient(sectionKey, name);
    if (!ing || !ing.costPerUnit || ing.costPerUnit === 0) return 0;
    // Casings are priced per yard/foot — there is no grams conversion, and
    // falling back to 1 would read $/yd as $/g. Use costPerFoot instead.
    const conversionFactor = UNIT_TO_G[ing.costUnit];
    if (!conversionFactor) return 0;
    return ing.costPerUnit / conversionFactor;
  }, [getIngredient]);

  // ── Cost per foot, for casings (sold by the yard or the foot) ──────────
  const costPerFoot = useCallback((name) => {
    const ing = getIngredient('casing', name);
    if (!ing || !(ing.costPerUnit > 0)) return 0;
    return ing.costPerUnit / (ing.costUnit === 'yd' ? 3 : 1);
  }, [getIngredient]);

  // ── Reset to defaults ──────────────────────────────────────
  const resetToDefaults = useCallback(() => {
    const fresh = buildDefaultIngredientDB();
    updateDb(fresh);
  }, [updateDb]);

  // ── Flat list of all ingredients (for search/overview) ────
  const allIngredients = useMemo(() => {
    const list = [];
    SECTIONS.forEach(sect => {
      (db[sect.key] || []).forEach(ing => {
        list.push({ ...ing, sectionKey: sect.key, sectionLabel: sect.label, sectionColor: sect.color });
      });
    });
    return list;
  }, [db]);

  return {
    db,
    allIngredients,
    getNamesForSection,
    getIngredient,
    updateIngredient,
    updateIngredientFull,
    addIngredient,
    removeIngredient,
    searchIngredients,
    costPerGram,
    costPerFoot,
    resetToDefaults,
  };
}
